import { useState } from 'react';

import { Icon, Tooltip } from '@components';
import {
  getTermStatusDisplay,
  isTermDisabled,
  segmentConnectorClass
} from '@features/transactions/helpers/installmentSchedule';
import { PaymentOrderTermType } from '@types';

import InstallmentTermCard from './InstallmentTermCard';
import { shouldShowTermInstallmentAmountTooltip } from './TermInstallmentBreakdownTooltip';

type InstallmentTermRowProps = {
  row: PaymentOrderTermType;
  index: number;
  rows: PaymentOrderTermType[];
  memberId: string;
  amountSymbolClass: string;
  isDefaultCurrency: boolean;
  paymentLinkNotGenerated: boolean;
};

const TermTimelineColumn = ({
  row,
  index,
  rows,
  iconName
}: {
  row: PaymentOrderTermType;
  index: number;
  rows: PaymentOrderTermType[];
  iconName: string;
}) => (
  <div className="flex w-6 shrink-0 flex-col items-center self-stretch">
    {index > 0 ? (
      <div
        className={`h-4 w-0 shrink-0 border-l-2 border-dashed ${segmentConnectorClass(
          rows[index - 1]
        )}`}
      />
    ) : null}
    <Icon
      name={iconName}
      className="size-8 gap-[7.35px] rounded-full p-[1.47px] opacity-100"
    />
    {index < rows.length - 1 ? (
      <div
        className={`mt-0 min-h-10 w-0 flex-1 border-l-2 border-dashed ${segmentConnectorClass(
          row
        )}`}
      />
    ) : null}
  </div>
);

const InstallmentTermRow = ({
  row,
  index,
  rows,
  memberId,
  amountSymbolClass,
  isDefaultCurrency,
  paymentLinkNotGenerated
}: InstallmentTermRowProps) => {
  const [isAmountHovered, setIsAmountHovered] = useState(false);

  const termKey = row.term_number ?? index;
  const statusTooltipId = `installment-status-${index}-${termKey}`;

  const showBreakdownTooltip = shouldShowTermInstallmentAmountTooltip(
    row,
    String(row?.amount ?? 0)
  );

  const showStatusTooltip =
    isTermDisabled(row) || !(showBreakdownTooltip && isAmountHovered);
  const statusDisplay = getTermStatusDisplay(row, paymentLinkNotGenerated);

  return (
    <>
      <div
        className="flex gap-3"
        data-tooltip-id={showStatusTooltip ? statusTooltipId : undefined}
      >
        <TermTimelineColumn
          row={row}
          index={index}
          rows={rows}
          iconName={statusDisplay.timelineIcon}
        />
        <div className="min-w-0 flex-1 pb-5">
          <InstallmentTermCard
            row={row}
            index={index}
            memberId={memberId}
            amountSymbolClass={amountSymbolClass}
            isDefaultCurrency={isDefaultCurrency}
            onAmountHoverChange={setIsAmountHovered}
            paymentLinkNotGenerated={paymentLinkNotGenerated}
          />
        </div>
      </div>

      {showStatusTooltip ? (
        <Tooltip
          id={statusTooltipId}
          type="custom"
          variant="dark"
          place="top"
          className="!max-w-90"
        >
          <div className="flex items-start gap-2 p-1">
            <Icon
              name={statusDisplay.tooltipIcon}
              size="medium"
              className="shrink-0"
            />
            <span className="text-sm font-normal">
              {statusDisplay.tooltipText}
            </span>
          </div>
        </Tooltip>
      ) : null}
    </>
  );
};

export default InstallmentTermRow;
